import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getJSON, postJSON, type AdminSwapPoolRow, type AdminSwapTokenRow, type MessageResponse } from '../lib/api';
import {
  inputMonoStackFieldClass,
  outlineSoftButtonClass,
  primarySubmitButtonClass,
  secondaryDarkButtonInlineClass,
  selectCompactFieldClass,
  selectStackFieldClass,
} from './form/fieldClasses';
import { explorerAddressUrl, explorerTokenUrl } from '../lib/explorers';

const CHAINS = ['ethereum', 'sepolia', 'bsc', 'base', 'avax', 'polygon', 'arb', 'op'] as const;

const HEX40 = /^[0-9a-fA-F]{40}$/;

/** token0 / token1 order: numeric compare on 40-hex refs (Uniswap-style); otherwise lexicographic. */
function pairToken0Token1(tokenIn: string, tokenOut: string): [string, string] {
  const a = tokenIn.trim();
  const b = tokenOut.trim();
  const cmp = (): number => {
    if (HEX40.test(a) && HEX40.test(b)) {
      const ba = BigInt(`0x${a}`);
      const bb = BigInt(`0x${b}`);
      if (ba < bb) return -1;
      if (ba > bb) return 1;
      return 0;
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  };
  return cmp() <= 0 ? [a, b] : [b, a];
}

function formatPoolRef(ref: string): string {
  const r = ref.trim();
  return r === 'native' ? 'native' : `0x${r}`;
}

function tokenKey(chain: string, ref: string): string {
  const norm = ref.trim().toLowerCase().replace(/^0x/, '');
  return `${chain.trim().toLowerCase()}\t${norm}`;
}

/** Avoid "Uniswap V3 v3" when protocol name already ends with the version tag. */
function poolProtocolLabel(protocol: string, version: string): string {
  const p = protocol.trim();
  const v = version.trim().toLowerCase();
  if (!v) return p;
  const pl = p.toLowerCase();
  if (pl.endsWith(v)) return p;
  return `${p} ${v}`;
}

type Props = {
  t: (key: string, options?: Record<string, unknown>) => string;
  busy: boolean;
  setBusy: (v: boolean) => void;
  setMsg: (s: string) => void;
  setMsgOk: (v: boolean) => void;
  resetMessage: () => void;
};

type TokenSuggestInputProps = {
  value: string;
  selectedAddress: string;
  placeholder: string;
  options: AdminSwapTokenRow[];
  onInput: (v: string) => void;
  onSelect: (address: string, label: string) => void;
};

function TokenSuggestInput({ value, selectedAddress, placeholder, options, onInput, onSelect }: TokenSuggestInputProps) {
  const [open, setOpen] = useState(false);
  const queryRaw = selectedAddress ? '' : value.trim().toLowerCase();
  const query = queryRaw.replace(/^0x/, '');
  const filtered = useMemo(() => {
    if (!queryRaw) {
      return options.slice(0, 50);
    }
    const rows = options.filter((tr) => {
      const addr = tr.address.toLowerCase();
      const sym = tr.symbol.toLowerCase();
      const nm = tr.name.toLowerCase();
      return addr.includes(query) || sym.includes(queryRaw) || nm.includes(queryRaw);
    });
    return rows.slice(0, 50);
  }, [options, query, queryRaw]);
  return (
    <div className="relative">
      <input
        className={`${inputMonoStackFieldClass} pr-8`}
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 100)}
        onChange={(e) => {
          onInput(e.target.value);
          setOpen(true);
        }}
      />
      <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-slate-400">v</span>
      {open ? (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {filtered.length > 0 ? (
            filtered.map((tr) => (
              <button
                key={`${tr.chain}-${tr.address}`}
                type="button"
                className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-slate-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(tr.address, `${tr.symbol} - ${tr.name}`);
                  setOpen(false);
                }}
                title={`${tr.symbol} - ${tr.name}`}
              >
                {tr.symbol} - {tr.name}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-slate-500">No matched token</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function AdminSwapTokensPanel({ t, busy, setBusy, setMsg, setMsgOk, resetMessage }: Props) {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<AdminSwapTokenRow[]>([]);
  const [chain, setChain] = useState<string>('ethereum');
  const [contract, setContract] = useState('');

  const load = useCallback(async () => {
    const { res, data } = await getJSON<{ tokens?: AdminSwapTokenRow[]; error?: string }>('/api/admin/swap/tokens');
    if (!res.ok) {
      setMsgOk(false);
      setMsg(data.error || t('common.error'));
      return;
    }
    setRows(data.tokens ?? []);
  }, [setMsg, setMsgOk, t, i18n.language]);

  useEffect(() => {
    void load();
  }, [load]);

  async function postAction(body: Record<string, unknown>) {
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/swap/tokens', body);
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function onFromContract(e: FormEvent) {
    e.preventDefault();
    await postAction({
      action: 'from_contract',
      chain,
      contract: contract.trim(),
    });
  }

  return (
    <div className="grid gap-6">
      <p className="text-sm text-slate-600">{t('admin.swapTokensHint')}</p>

      <form
        className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 md:grid-cols-[1fr_2fr_auto]"
        onSubmit={(e) => void onFromContract(e)}
      >
        <div className="md:col-span-3 text-sm font-medium text-slate-800">{t('admin.swapTokenFromContract')}</div>
        <select className={selectStackFieldClass} value={chain} onChange={(e) => setChain(e.target.value)}>
          {CHAINS.map((c) => (
            <option key={c} value={c}>
              {t(`wallet.chain.${c}`)}
            </option>
          ))}
        </select>
        <input
          className={inputMonoStackFieldClass}
          placeholder="0x…"
          value={contract}
          onChange={(e) => setContract(e.target.value)}
        />
        <button type="submit" disabled={busy} className={secondaryDarkButtonInlineClass}>
          {t('admin.swapTokenImport')}
        </button>
      </form>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-[48rem] w-full border-collapse text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="border-b px-3 py-2">{t('admin.thId')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapChainCol')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapColAddress')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapSymbol')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapName')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapDecimals')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapColEnabled')}</th>
              <th className="border-b px-3 py-2">{t('admin.swapSource')}</th>
              <th className="border-b px-3 py-2">{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const addrLabel = r.address === 'native' ? 'native' : `0x${r.address}`;
              const explorerHref = explorerTokenUrl(r.chain, r.address);
              return (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="px-3 py-2 tabular-nums">{r.id}</td>
                <td className="px-3 py-2">{r.chain}</td>
                <td className="px-3 py-2 font-mono text-xs break-all">
                  {explorerHref ? (
                    <a
                      href={explorerHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('wallet.explorerTitle')}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {addrLabel}
                    </a>
                  ) : (
                    <span>{addrLabel}</span>
                  )}
                </td>
                <td className="px-3 py-2">{r.symbol}</td>
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2">{r.decimals}</td>
                <td className="px-3 py-2">
                  {r.enabled ? (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-800">{t('common.yes')}</span>
                  ) : (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{t('common.no')}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{r.source}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      disabled={busy || r.address === 'native'}
                      className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-40"
                      onClick={() => void postAction({ action: 'sync_from_chain', id: r.id })}
                    >
                      {t('admin.swapSyncChain')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50"
                      onClick={() => void postAction({ action: 'set_enabled', id: r.id, enabled: !r.enabled })}
                    >
                      {r.enabled ? t('admin.swapDisable') : t('admin.swapEnable')}
                    </button>
                    {r.source === 'admin' ? (
                      <button
                        type="button"
                        disabled={busy}
                        className="rounded border border-red-200 bg-white px-2 py-0.5 text-xs text-red-700 hover:bg-red-50"
                        onClick={() => void postAction({ action: 'delete', id: r.id })}
                      >
                        {t('admin.swapDelete')}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminSwapPoolsPanel({ t, busy, setBusy, setMsg, setMsgOk, resetMessage }: Props) {
  const { i18n } = useTranslation();
  const [rows, setRows] = useState<AdminSwapPoolRow[]>([]);
  const [tokenRows, setTokenRows] = useState<AdminSwapTokenRow[]>([]);
  const [chain, setChain] = useState('ethereum');
  const [tokenIn, setTokenIn] = useState('');
  const [tokenOut, setTokenOut] = useState('');
  const [tokenInAddr, setTokenInAddr] = useState('');
  const [tokenOutAddr, setTokenOutAddr] = useState('');
  const [poolChainFilter, setPoolChainFilter] = useState<string>('all');
  const [poolPairFilter, setPoolPairFilter] = useState<string>('all');
  const [poolLoading, setPoolLoading] = useState(false);

  const load = useCallback(async () => {
    setPoolLoading(true);
    try {
      const [poolsResp, tokensResp] = await Promise.all([
        getJSON<{ pools?: AdminSwapPoolRow[]; error?: string }>('/api/admin/swap/pools'),
        getJSON<{ tokens?: AdminSwapTokenRow[]; error?: string }>('/api/admin/swap/tokens'),
      ]);
      if (!poolsResp.res.ok) {
        setMsgOk(false);
        setMsg(poolsResp.data.error || t('common.error'));
        return;
      }
      setRows(poolsResp.data.pools ?? []);
      if (tokensResp.res.ok) {
        setTokenRows(tokensResp.data.tokens ?? []);
      }
    } finally {
      setPoolLoading(false);
    }
  }, [setMsg, setMsgOk, t, i18n.language]);

  useEffect(() => {
    void load();
  }, [load]);

  const tokenSymbolMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tr of tokenRows) {
      const sym = tr.symbol.trim();
      if (sym) {
        m.set(tokenKey(tr.chain, tr.address), sym);
      }
    }
    return m;
  }, [tokenRows]);

  function poolPairKey(chainName: string, tokenIn: string, tokenOut: string): string {
    const [t0, t1] = pairToken0Token1(tokenIn, tokenOut);
    return `${chainName}\t${t0}\t${t1}`;
  }

  function poolPairLabel(chainName: string, t0: string, t1: string, withChain: boolean): string {
    const l0 = tokenSymbolMap.get(tokenKey(chainName, t0)) ?? formatPoolRef(t0);
    const l1 = tokenSymbolMap.get(tokenKey(chainName, t1)) ?? formatPoolRef(t1);
    const pair = `${l0} / ${l1}`;
    return withChain ? `${chainName} · ${pair}` : pair;
  }

  const poolPairOptions = useMemo(() => {
    const seen = new Map<string, string>();
    const source = poolChainFilter === 'all' ? rows : rows.filter((r) => r.chain === poolChainFilter);
    for (const r of source) {
      const [t0, t1] = pairToken0Token1(r.token_in_address, r.token_out_address);
      const key = poolPairKey(r.chain, r.token_in_address, r.token_out_address);
      if (!seen.has(key)) {
        seen.set(key, poolPairLabel(r.chain, t0, t1, poolChainFilter === 'all'));
      }
    }
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows, poolChainFilter, tokenSymbolMap]);

  useEffect(() => {
    if (poolPairFilter === 'all') {
      return;
    }
    if (!poolPairOptions.some((o) => o.key === poolPairFilter)) {
      setPoolPairFilter('all');
    }
  }, [poolPairFilter, poolPairOptions]);

  const filteredPoolRows = useMemo(() => {
    let list = poolChainFilter === 'all' ? rows : rows.filter((r) => r.chain === poolChainFilter);
    if (poolPairFilter !== 'all') {
      const parts = poolPairFilter.split('\t');
      if (parts.length === 3) {
        const [chainName, t0, t1] = parts;
        list = list.filter((r) => {
          const [a, b] = pairToken0Token1(r.token_in_address, r.token_out_address);
          return r.chain === chainName && a === t0 && b === t1;
        });
      }
    }
    return list;
  }, [rows, poolChainFilter, poolPairFilter]);

  const chainTokenOptions = useMemo(
    () =>
      tokenRows
        .filter((tr) => tr.chain === chain)
        .sort((a, b) => a.symbol.localeCompare(b.symbol) || a.address.localeCompare(b.address)),
    [tokenRows, chain],
  );

  async function postAction(body: Record<string, unknown>) {
    resetMessage();
    setBusy(true);
    try {
      const { res, data } = await postJSON<MessageResponse>('/api/admin/swap/pools', body);
      if (!res.ok) {
        setMsgOk(false);
        setMsg(data.error || t('common.error'));
        return;
      }
      setMsgOk(true);
      setMsg(data.message || '');
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    const body: Record<string, unknown> = {
      action: 'create',
      chain,
      token_in_address: (tokenInAddr || tokenIn).trim(),
      token_out_address: (tokenOutAddr || tokenOut).trim(),
    };
    await postAction(body);
  }

  return (
    <div className="grid gap-6">
      <p className="text-sm text-slate-600">{t('admin.swapPoolsHint')}</p>

      <form
        className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50/80 p-4 md:grid-cols-3"
        onSubmit={(e) => void onCreate(e)}
      >
        <div className="md:col-span-3 text-sm font-medium text-slate-800">{t('admin.swapPoolCreate')}</div>
        <select
          className={selectStackFieldClass}
          value={chain}
          onChange={(e) => {
            setChain(e.target.value);
            setTokenIn('');
            setTokenOut('');
            setTokenInAddr('');
            setTokenOutAddr('');
          }}
        >
          {CHAINS.map((c) => (
            <option key={c} value={c}>
              {t(`wallet.chain.${c}`)}
            </option>
          ))}
        </select>
        <TokenSuggestInput
          placeholder={t('admin.swapPoolIn')}
          value={tokenIn}
          selectedAddress={tokenInAddr}
          options={chainTokenOptions}
          onInput={(v) => {
            setTokenIn(v);
            setTokenInAddr('');
          }}
          onSelect={(address, label) => {
            setTokenIn(label);
            setTokenInAddr(address);
          }}
        />
        <TokenSuggestInput
          placeholder={t('admin.swapPoolOut')}
          value={tokenOut}
          selectedAddress={tokenOutAddr}
          options={chainTokenOptions}
          onInput={(v) => {
            setTokenOut(v);
            setTokenOutAddr('');
          }}
          onSelect={(address, label) => {
            setTokenOut(label);
            setTokenOutAddr(address);
          }}
        />
        <button type="submit" disabled={busy} className={primarySubmitButtonClass}>
          {t('admin.swapPoolSave')}
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{t('admin.swapPoolFilterChain')}</span>
          <select
            className={selectCompactFieldClass}
            value={poolChainFilter}
            onChange={(e) => {
              setPoolChainFilter(e.target.value);
              setPoolPairFilter('all');
            }}
          >
          <option value="all">{t('admin.swapPoolAllChains')}</option>
          {CHAINS.map((c) => (
            <option key={c} value={c}>
              {t(`wallet.chain.${c}`)}
            </option>
          ))}
          </select>
        </label>
        <label className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{t('admin.swapPoolFilterPair')}</span>
          <select
            className={selectCompactFieldClass}
            value={poolPairFilter}
            onChange={(e) => setPoolPairFilter(e.target.value)}
          >
            <option value="all">{t('admin.swapPoolAllPairs')}</option>
            {poolPairOptions.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={busy || poolLoading}
          className={outlineSoftButtonClass}
          onClick={() => void load()}
        >
          {poolLoading ? t('admin.swapPoolRefreshing') : t('admin.swapPoolRefresh')}
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="min-w-[56rem] w-full border-collapse text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="border-b px-2 py-2">{t('admin.thId')}</th>
              {poolChainFilter === 'all' ? (
                <th className="border-b px-2 py-2">{t('admin.swapChainCol')}</th>
              ) : null}
              <th className="border-b px-2 py-2">{t('admin.swapColProtocol')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapColToken0')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapColToken1')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapColFee')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapColPool')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapColEnabled')}</th>
              <th className="border-b px-2 py-2">{t('admin.swapSource')}</th>
              <th className="border-b px-2 py-2">{t('admin.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPoolRows.map((r) => {
              const [t0, t1] = pairToken0Token1(r.token_in_address, r.token_out_address);
              const token0Href = explorerTokenUrl(r.chain, t0);
              const token1Href = explorerTokenUrl(r.chain, t1);
              const poolHref = r.pool_address ? explorerAddressUrl(r.chain, r.pool_address) : null;
              const token0Label = tokenSymbolMap.get(tokenKey(r.chain, t0)) ?? formatPoolRef(t0);
              const token1Label = tokenSymbolMap.get(tokenKey(r.chain, t1)) ?? formatPoolRef(t1);
              return (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="px-2 py-2 tabular-nums">{r.id}</td>
                {poolChainFilter === 'all' ? <td className="px-2 py-2">{r.chain}</td> : null}
                <td className="px-2 py-2">{poolProtocolLabel(r.protocol, r.version)}</td>
                <td className="px-2 py-2 font-mono text-xs break-all">
                  {token0Href ? (
                    <a
                      href={token0Href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('wallet.explorerTitle')}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {token0Label}
                    </a>
                  ) : (
                    <span>{token0Label}</span>
                  )}
                </td>
                <td className="px-2 py-2 font-mono text-xs break-all">
                  {token1Href ? (
                    <a
                      href={token1Href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('wallet.explorerTitle')}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {token1Label}
                    </a>
                  ) : (
                    <span>{token1Label}</span>
                  )}
                </td>
                <td className="px-2 py-2">{r.fee_tier ?? '—'}</td>
                <td className="px-2 py-2 font-mono text-xs break-all">
                  {poolHref ? (
                    <a
                      href={poolHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('swap.openPoolInExplorer')}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {r.pool_address}
                    </a>
                  ) : (
                    <span>{r.pool_address || '—'}</span>
                  )}
                </td>
                <td className="px-2 py-2">
                  {r.enabled ? (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-800">{t('common.yes')}</span>
                  ) : (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{t('common.no')}</span>
                  )}
                </td>
                <td className="px-2 py-2 text-xs">{r.source}</td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50"
                      onClick={() => void postAction({ action: 'sync_pool_address', id: r.id })}
                    >
                      {t('admin.swapPoolSyncAddr')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs hover:bg-slate-50"
                      onClick={() => void postAction({ action: 'set_enabled', id: r.id, enabled: !r.enabled })}
                    >
                      {r.enabled ? t('admin.swapDisable') : t('admin.swapEnable')}
                    </button>
                    {r.source === 'admin' ? (
                      <button
                        type="button"
                        disabled={busy}
                        className="rounded border border-red-200 bg-white px-2 py-0.5 text-xs text-red-700 hover:bg-red-50"
                        onClick={() => void postAction({ action: 'delete', id: r.id })}
                      >
                        {t('admin.swapDelete')}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
